import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ChevronDown, ChevronUp, Wand2 } from "lucide-react";

const esc = (s) =>
  String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * Break a pasted judgement body into paragraphs. Splits on blank lines OR on
 * sentence-ending punctuation followed by two+ spaces. Every paragraph is
 * emitted with `text-align: justify` — the House-style default for judgement
 * body copy. Single `\n` inside a paragraph is preserved via `<br>` so admins
 * don't have to babysit the paste.
 */
function formatBody(text) {
  const clean = String(text || "").replace(/\r\n?/g, "\n").trim();
  if (!clean) return "";
  const paras = clean.split(/\n{2,}/);
  return paras
    .map((p) => `<p style="text-align: justify; text-indent: 2em; margin: 0.6em 0;">${esc(p).replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/**
 * Compile the structured fields into the House-style judgement HTML the
 * student sees. Uses the existing global `.cause-title` and `.counsel-columns`
 * classes so admin editor + admin preview + student source all render byte-
 * identical (no duplicate CSS). Centred, bolded court + case no. sit on top,
 * followed by the cause title rows, optional counsel columns, then the
 * justified body.
 */
export function compileJudgementHtml({
  court, caseNo, appellant, respondent, appellantRole = "Appellant(s)",
  respondentRole = "Respondent(s)", appellantCounsel = "", respondentCounsel = "", body = "",
}) {
  const parts = [];
  if (court) parts.push(`<div style="text-align:center;"><b>${esc(court.toUpperCase())}</b></div>`);
  if (caseNo) parts.push(`<div style="text-align:center;"><b>${esc(caseNo)}</b></div>`);
  if (court || caseNo) parts.push("<br>");
  if (appellant) {
    parts.push(
      `<div class="cause-title"><span class="party">${esc(appellant)}</span>` +
      `<span class="leader"></span><span class="role">…${esc(appellantRole)}</span></div>`
    );
  }
  if (appellant && respondent) {
    parts.push(`<div style="text-align:center;"><i>Versus</i></div>`);
  }
  if (respondent) {
    parts.push(
      `<div class="cause-title"><span class="party">${esc(respondent)}</span>` +
      `<span class="leader"></span><span class="role">…${esc(respondentRole)}</span></div>`
    );
  }
  if (appellantCounsel || respondentCounsel) {
    parts.push(
      `<br><div class="counsel-columns">` +
      `<div><h4>For the ${esc(appellantRole)}</h4><div>${esc(appellantCounsel).replace(/\n/g, "<br>")}</div></div>` +
      `<div><h4>For the ${esc(respondentRole)}</h4><div>${esc(respondentCounsel).replace(/\n/g, "<br>")}</div></div>` +
      `</div>`
    );
  }
  const bodyHtml = formatBody(body);
  if (bodyHtml) {
    parts.push("<br>", bodyHtml);
  }
  return parts.join("");
}

/**
 * Structured admin form — the admin just fills in Title / Court / Case No /
 * Appellant / Respondent / Counsel / Body and clicks "Auto-compile". The
 * resulting HTML is written into the main passage form's content_html + plain
 * content fields so the existing "Add passage" button can save as-is.
 */
export default function QuickJudgementBuilder({ onCompile }) {
  const [open, setOpen] = useState(true);
  const [f, setF] = useState({
    title: "", court: "", caseNo: "", appellant: "", respondent: "",
    appellantRole: "Appellant(s)", respondentRole: "Respondent(s)",
    appellantCounsel: "", respondentCounsel: "", body: "", difficulty: "medium",
  });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const compile = () => {
    const html = compileJudgementHtml(f);
    if (!html) return;
    // Rough plain-text approximation for search / word count. The backend
    // recomputes word_count from `content`, so a strip-tags pass is enough.
    const plain = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|h[1-6])>/gi, "\n\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    onCompile?.({
      title: f.title || `${f.court} — ${f.caseNo}`.trim(),
      court: f.court,
      difficulty: f.difficulty,
      content_html: html,
      content: plain,
    });
  };

  const F = (label, key, placeholder, testId, span = "md:col-span-1") => (
    <div className={span}>
      <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{label}</Label>
      <Input value={f[key]} onChange={upd(key)} placeholder={placeholder} className="rounded-lg font-mono mt-1" data-testid={testId} />
    </div>
  );

  return (
    <div className="border-2 border-brand-gold/30 rounded-xl bg-brand-gold/5" data-testid="admin-legal-quickbuilder">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left"
        data-testid="admin-legal-quickbuilder-toggle"
      >
        <div className="flex items-center gap-3">
          <Sparkles className="w-4 h-4 text-brand-gold" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold">Quick judgement builder</div>
            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
              Fill in the structured fields and click Auto-compile. The form below will fill itself with a perfectly-formatted High Court layout.
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {F("Title / Exercise Name", "title", "Exercise-5", "quickbuilder-title")}
            {F("Court Name", "court", "HIGH COURT OF JUDICATURE AT ALLAHABAD", "quickbuilder-court")}
            {F("Case / Appeal No.", "caseNo", "CRIMINAL APPEAL No. — 3761 of 2026", "quickbuilder-caseno", "md:col-span-2")}
            {F("Appellant Name", "appellant", "Wasim Rahman", "quickbuilder-appellant")}
            {F("Respondent Name", "respondent", "State of U.P.", "quickbuilder-respondent")}
            {F("Appellant Role", "appellantRole", "Appellant(s)", "quickbuilder-appellant-role")}
            {F("Respondent Role", "respondentRole", "Respondent(s)", "quickbuilder-respondent-role")}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Counsel — Appellant side (optional)</Label>
              <Textarea value={f.appellantCounsel} onChange={upd("appellantCounsel")} placeholder="Mr. Sanjay Kumar Upadhyay, Sr. Adv.&#10;Ms. Priya Sharma, Adv." className="rounded-lg font-mono mt-1 min-h-20" data-testid="quickbuilder-appellant-counsel" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Counsel — Respondent side (optional)</Label>
              <Textarea value={f.respondentCounsel} onChange={upd("respondentCounsel")} placeholder="Mr. R. K. Verma, ASG&#10;Mr. Amit Singh, Adv." className="rounded-lg font-mono mt-1 min-h-20" data-testid="quickbuilder-respondent-counsel" />
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Main judgement body — paste plain text, we'll justify + indent every paragraph</Label>
            <Textarea
              value={f.body}
              onChange={upd("body")}
              placeholder={"Heard the learned counsel appearing for the appellant and the learned counsel appearing for the respondent-State.\n\nIssue notice returnable in six weeks. In the meantime, there shall be an interim stay of the impugned judgement and order dated 14 August 2025 passed by the High Court of Judicature at Allahabad in Criminal Miscellaneous Application No. 5678 of 2025.\n\nList in due course."}
              className="rounded-lg font-mono mt-1 min-h-48"
              data-testid="quickbuilder-body"
            />
          </div>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Difficulty</Label>
                <Select value={f.difficulty} onValueChange={(v) => setF({ ...f, difficulty: v })}>
                  <SelectTrigger className="rounded-lg w-36 mt-1" data-testid="quickbuilder-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              type="button"
              onClick={compile}
              disabled={!f.body.trim() && !f.court.trim()}
              className="rounded-lg h-11 px-6 bg-brand-gold hover:bg-[var(--brand-gold-hover)] text-white text-xs uppercase tracking-[0.2em] font-bold"
              data-testid="quickbuilder-compile-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" /> Auto-compile into standard layout
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
